import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDirection } from "@/hooks/use-direction";
import {
  LayoutDashboard,
  CalendarCheck,
  MapPin,
  BarChart3,
  Zap,
  Settings,
  LifeBuoy,
  LogOut,
  Package,
  DollarSign,
  Wallet,
  Crown,
  Tags,
} from "lucide-react";

import { Logo } from "@/components/brand/logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

const activeClass =
  "bg-[#5958F3] text-white hover:bg-[#5958F3]/90 hover:text-white data-[active=true]:bg-[#5958F3] data-[active=true]:text-white";

export function MerchantSidebar() {
  const { t } = useTranslation();
  const dir = useDirection();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = (url: string) =>
    url === "/merchant" ? pathname === "/merchant" : pathname.startsWith(url);
  const side = dir === "rtl" ? "right" : "left";

  const main = [
    { title: t("merchant.nav.dashboard"), url: "/merchant", icon: LayoutDashboard },
    { title: t("merchant.nav.bookings"), url: "/merchant/bookings", icon: CalendarCheck, badge: "3" },
    { title: t("merchant.nav.spaces"), url: "/merchant/spaces", icon: MapPin },
    { title: t("merchant.nav.products"), url: "/merchant/products", icon: Package },
    { title: t("merchant.nav.fastAction"), url: "/merchant/fast-action", icon: Zap },
  ];
  const insights = [
    { title: t("merchant.nav.analytics"), url: "/merchant/analytics", icon: BarChart3 },
    { title: t("merchant.nav.revenue"), url: "/merchant/revenue", icon: DollarSign },
    { title: t("merchant.nav.payouts"), url: "/merchant/payouts", icon: Wallet },
    { title: t("merchant.nav.subscription"), url: "/merchant/subscription", icon: Crown },
    { title: t("merchant.nav.clientPlans"), url: "/merchant/client-plans", icon: Tags },
  ];
  const footer = [
    { title: t("merchant.nav.settings"), url: "/merchant/settings", icon: Settings },
    { title: t("merchant.nav.help"), url: "/help", icon: LifeBuoy },
  ];

  const renderGroup = (label: string, items: typeof main) => (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const active = isActive(item.url);
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  asChild
                  isActive={active}
                  tooltip={item.title}
                  className={active ? activeClass : ""}
                >
                  <Link to={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                    {"badge" in item && item.badge && !collapsed && (
                      <span
                        className={`ms-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                          active ? "bg-white/20 text-white" : "bg-primary text-primary-foreground"
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar side={side} collapsible="icon" className="border-e border-border/60">
      <SidebarHeader className="px-2 py-4">
        <Link
          to="/merchant"
          className={`flex items-center gap-2.5 ${collapsed ? "justify-center px-0" : "px-2"}`}
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground shadow-soft">
            <Logo className="h-5 w-5" mark />
          </span>
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold tracking-tight">Spayzo</p>
              <p className="truncate text-[11px] text-muted-foreground">{t("merchant.suite")}</p>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {renderGroup(t("merchant.groups.workspace"), main)}
        {renderGroup(t("merchant.groups.insights"), insights)}
      </SidebarContent>

      <SidebarFooter className="border-t border-border/60 p-2">
        <SidebarMenu>
          {footer.map((item) => {
            const active = isActive(item.url);
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  asChild
                  isActive={active}
                  tooltip={item.title}
                  className={active ? activeClass : ""}
                >
                  <Link to={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("merchant.signOut")}>
              <Link to="/login">
                <LogOut />
                <span>{t("merchant.signOut")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {!collapsed && (
          <div className="mt-2 flex items-center gap-2.5 rounded-xl bg-surface p-2.5">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-semibold text-primary">
              CC
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold">Central Coffee Co.</p>
              <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("merchant.openNow")}
              </p>
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
